/* eslint-disable no-alert */
import React, { useEffect, useContext, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { navigate } from '@reach/router';
import _ from 'lodash'

import JobListContext from '../../context/jobListContext';
import GetPMCallsFunction from "../../../functions/GetPMServiceCallsFunction";
import getServiceCallFunction from '../../../functions/GetServiceCallsFunction';
import GetReactiveCallPages from '../../../functions/GetReactiveCallPagesFunction';
import GetReactiveCallRecent from '../../../functions/GetReactiveCallRecentFunction';
import AppStateContext from '../../context/appStateContext';

import JobsList from '../../components/Lists/cardList';
import PageHeaderHome from '../../components/pageHeaderHome';
import PageLayout from '../../components/pageLayout';
import JobSummaryCardList from '../../components/Lists/jobSummaryCardList';
import SlaIndictorSum from '../../components/slaIndicatorSum';
import ApiStatus, {CALLING,POPULATED,EMPTY,TIMEOUT} from "../../../components/apiStatus";
import SectionLoadingLine from '../../../components/sectionLoadingLine';
import GhostCards from '../../components/Lists/ghostCard';
import filterHelper from '../../../hooks/useFilterHelper';
import APIHelper from "../../../hooks/useAPIHelper";
import { fetchPlannedAsOf, plannedAsOfFor, plannedListIsPartial, pmCallPagesEnabled } from '../../../functions/GetPMCallPagesFunction';
import GetPMCallSummary, { PMCallSummary } from '../../../functions/GetPMCallSummaryFunction';
import GetReactiveCallSummary, { ReactiveCallSummary } from '../../../functions/GetReactiveCallSummaryFunction';
import GetReactiveCallSlaSummary, { ReactiveCallSlaSummary } from '../../../functions/GetReactiveCallSlaSummaryFunction';
import { markPlannedLoaded, plannedLoadedFor, formatDataAsOf } from '../../../hooks/homeSession';

import Images from '../../../assets/images';
import brand from '../../../branding';

import {
  ContentCtn,
  FullWidthCtn,
  HeadingCtn,
  LinkToPage,
  PageCtn,
  PageHeaderImgCtn,
  PageHeaderText,
  PlannedListCtn,
  ReactiveListCtn,
} from './index.style';
import GetContractDataList from "../../../functions/GetContractDetailsFunction";

// The "data as at" note beside a heading. Its line is ALWAYS there: until the time is known the note is drawn but
// invisible, so the heading is the same height before and after and nothing below it moves when the time arrives (at
// narrower widths the note wraps onto its own line, which used to push the lists down by 23 px).
const DataAsAt: React.FC<{ asOf: string | null }> = ({ asOf }): React.ReactElement => {
  const known = formatDataAsOf(asOf);

  return <small style={known === '' ? { visibility: 'hidden' } : undefined}>data as at {known === '' ? '00 Jan, 0:00 am' : known}</small>;
};

const Home: React.FC<any> = (): React.ReactElement => {

  const { setAppPageDetails, setAppStateDetails, token, userId, selectedCustomerId, siteLogo, setContractCriticalCode, setContractCallers, setContractList, contractList } = useContext(AppStateContext);
  const { reactiveJobList, plannedJobList, filteredReactiveJobList, filteredPlannedJobList, updateFilteredReactiveJobList, updateReactiveJobList, updateFilteredPlannedJobList, updatePlannedJobList } = useContext(JobListContext);

  // WHAT THIS SESSION ALREADY HOLDS (PORTAL-72). Coming back to Home used to repeat every call, with the same
  // wait, for data that cannot have changed: planned maintenance moves only when the nightly extract does.
  // The lists live in the job-list context already, so only the fact that planned was loaded for THIS sign-in
  // and customer needs remembering (hooks/homeSession). Reactive calls can change at any time and are always
  // refreshed; they just no longer blank the screen while they are.
  const plannedCached = plannedLoadedFor(token, selectedCustomerId);
  const havePlanned = plannedCached !== null && plannedJobList.length > 0;
  const haveReactive = reactiveJobList.length > 0;
  const haveContracts = !_.isArray(contractList[selectedCustomerId]) && _.isObject(contractList[selectedCustomerId]);

  const [ reactiveApiStatus, setReactiveApiStatus ] = useState(haveReactive ? POPULATED : CALLING);

  // THE RECENT LIST (PORTAL-199). Home shows four calls, so where the site is set up for it they come from one small
  // query for the newest few (GetReactiveCallRecent) instead of waiting for the customer's whole paged set, which is
  // rebuilt in full every ten minutes it is in use. The SLA indicator's counts come from the database too
  // (GetReactiveCallSlaSummary), so Home no longer reads the paged set at all. The one exception is a person the
  // database finds no entitlement for: the recent query then answers null, and the list and the indicator are taken
  // from the paged set, filtered by contract, as they always were.
  const [ recentJobs, setRecentJobs ] = useState<any[] | null>(null);
  const [ recentStatus, setRecentStatus ] = useState(haveReactive ? POPULATED : CALLING);
  const [ recentFromPages, setRecentFromPages ] = useState(false);

  // The SLA indicator's counts from the database (PORTAL-199). Null until they arrive, or for good where they cannot be
  // had; slaAnswered says whether the call has finished either way, so a failure stops showing as "still loading".
  const [ slaSummary, setSlaSummary ] = useState<ReactiveCallSlaSummary | null>(null);
  const [ slaAnswered, setSlaAnswered ] = useState(false);
  const [ plannedApiStatus, setPlannedApiStatus ] = useState(havePlanned ? POPULATED : CALLING);
  const [ immediateApiStatus, setImmediateApiStatus] = useState(havePlanned || haveReactive ? POPULATED : CALLING);
  const [ plannedAsOf, setPlannedAsOf ] = useState<string | null>(plannedCached !== null ? plannedCached.asOf : null);
  const [ logo, setLogo ] = useState('');

  // The planned counts from the database (PORTAL-72). Null until they arrive, or for good where they cannot be
  // had; the cards then count the list in the browser as they always did.
  const [ plannedSummary, setPlannedSummary ] = useState<PMCallSummary | null>(null);

  // The reactive counts from the database (PORTAL-199), held and used the same way. The reactive LIST is still the
  // live call, which carries the addresses and notes the database does not hold, so it keeps loading behind the
  // cards; the cards, which need only the counts, no longer wait for it.
  const [ reactiveSummary, setReactiveSummary ] = useState<ReactiveCallSummary | null>(null);

  const { filterDataByContract } = filterHelper();
  const apiHelper = APIHelper();

  const reactiveCallback = (data: [] | string) => {

    // A refresh that fails leaves the list already on screen alone, rather than replacing it with an error.
    if(data == 'timeout') {
      if(!haveReactive) {
        setReactiveApiStatus(TIMEOUT);
      }
      return;
    }

    if(data.length == 0) {
      setReactiveApiStatus(EMPTY);
      return;
    }

    updateFilteredReactiveJobList(filterDataByContract(data));
    updateReactiveJobList(filterDataByContract(data));
    setReactiveApiStatus(POPULATED);
  };

  const recentCallback = (data: any[] | null | string) => {

    // No entitlement found for the person: take the list, and the SLA counts, from the paged set, as before. This is
    // the only place Home asks for the paged set.
    if(data === null) {
      setRecentFromPages(true);
      apiHelper.run(GetReactiveCallPages, reactiveCallback, [token, selectedCustomerId]);
      return;
    }

    // A refresh that fails leaves the list already on screen alone, rather than replacing it with an error.
    if(typeof data === 'string') {
      if(recentJobs === null && !haveReactive) {
        setRecentStatus(TIMEOUT);
      }
      return;
    }

    if(data.length == 0) {
      setRecentStatus(EMPTY);
      return;
    }

    setRecentJobs(data);
    setRecentStatus(POPULATED);
  };

  const slaSummaryCallback = (data: ReactiveCallSlaSummary | null) => {
    setSlaSummary(data);
    setSlaAnswered(true);
  };

  const plannedCallback = (data: [] | string) => {

    if(data == 'timeout') {
      if(!havePlanned) {
        setPlannedApiStatus(TIMEOUT);
      }
      return;
    }

    if(data.length == 0) {
      setPlannedApiStatus(EMPTY);
      return;
    }

    updateFilteredPlannedJobList(filterDataByContract(data));
    updatePlannedJobList(filterDataByContract(data));
    setPlannedApiStatus(POPULATED);

    // Remember it for this sign-in, with the time the data was copied (null from the live call, which has none).
    const asOf = plannedAsOfFor(selectedCustomerId);

    markPlannedLoaded(token, selectedCustomerId, asOf, plannedListIsPartial(selectedCustomerId));
    setPlannedAsOf(asOf);
  };

  // Planned is read again only if the extract has moved since it was loaded. For a list that came from the live
  // call there is no time to compare, so it is kept until sign-out - the same data the live call would return.
  const refreshPlannedIfChanged = async (firstPageOnly: boolean) => {
    if(plannedCached === null || plannedCached.asOf === null) {
      return;
    }

    const current = await fetchPlannedAsOf(token, selectedCustomerId);

    if(typeof current === 'string' && current !== plannedCached.asOf) {
      apiHelper.run(GetPMCallsFunction, plannedCallback,['',token, selectedCustomerId, firstPageOnly]);
    }
  };

  const reactiveSummaryCallback = (data: ReactiveCallSummary | null | string | []) => {
    if(data !== null && typeof data === 'object' && !Array.isArray(data)) {
      setReactiveSummary(data as ReactiveCallSummary);
    }
  };

  const summaryCallback = (data: PMCallSummary | null | string | []) => {
    if(data !== null && typeof data === 'object' && !Array.isArray(data)) {
      setPlannedSummary(data as PMCallSummary);
    }
  };

  // Counts first, then only as much of the list as Home shows (PORTAL-72). With the database's counts in hand the
  // cards need none of the list, so only the first page - the newest rows - is read. Without them the cards count
  // the list in the browser, so the whole of it is read, exactly as before. A list held from an earlier visit
  // that is only a first page is not trusted for counting and is read in full if the counts are not available.
  const loadPlanned = async () => {
    let haveCounts = false;
    let firstPage: any = undefined;

    const heldIsPartial = plannedCached !== null && plannedCached.partial;
    const needRead = !havePlanned || heldIsPartial;

    // The counts and the first page are asked for TOGETHER where a read is needed, since the first page is
    // wanted whenever the counts arrive and each takes a few seconds. If the counts do not arrive the first
    // page is thrown away and the whole list is read, so the only cost of being wrong is one wasted page.
    const reads: Promise<any>[] = [];

    if(pmCallPagesEnabled()) {
      reads.push(apiHelper.run(GetPMCallSummary, (data: any) => {
        haveCounts = data !== null && typeof data === 'object' && !Array.isArray(data);
        summaryCallback(data);
      }, [token, userId, selectedCustomerId]));

      if(needRead) {
        reads.push(apiHelper.run(GetPMCallsFunction, (data: any) => { firstPage = data; }, ['', token, selectedCustomerId, true]));
      }
    }

    await Promise.all(reads);

    if(!needRead) {
      refreshPlannedIfChanged(haveCounts);
    } else if(haveCounts && Array.isArray(firstPage)) {
      plannedCallback(firstPage);
    } else {
      apiHelper.run(GetPMCallsFunction, plannedCallback,['',token, selectedCustomerId, false]);
    }
  };

  const contractListCallback = (data: any[] | string) => {
    let contractDepartments = {};

    data.map((contract) => {
      if(contract.ContractDepartments != "") contractDepartments[contract.ContractNo] = contract.ContractDepartments.ContractDepartment;
    });

    // A customer can have contracts where ContractCriticalCodes is genuinely "" (no codes
    // configured on that particular contract) sitting ahead of one that does carry them - taking
    // only data[0] silently left the hardcoded default (appStateProvider's PM1-4 "Critical
    // Risk"/"High Risk"/"Medium Risk"/"Low Risk") on screen, which reads exactly like real
    // customer data but isn't. Use the first contract that actually has codes instead.
    const contractWithCriticalCodes = data.find((contract) => contract.ContractCriticalCodes != "");
    let contractCriticalCodes = contractWithCriticalCodes ? contractWithCriticalCodes.ContractCriticalCodes.ContractCriticalCode : {};

    if(!_.isEmpty(contractCriticalCodes)) {
      setContractCriticalCode(contractCriticalCodes);
    }

    if(Object.keys(contractDepartments).length) {
      setAppStateDetails({
        contractDepartments: contractDepartments,
      })
    }

    if(data.length != 0) {
      setContractCallers(data);
      computeNewContractList(data);
    }
  };

  const computeNewContractList = (data: []) => {
    let newContractDetails = {};
    let contractData = _.assign({},contractList);

    let tempContractList = _.isArray(contractList[selectedCustomerId])? contractList[selectedCustomerId]:  Object.keys(contractList[selectedCustomerId]);

    tempContractList.map((item) => {
      const contractInfo = data.find(element => {
        return element.ContractNo == item;
      });
      if (contractInfo == undefined) return;
      newContractDetails[item] = {
        Description: contractInfo.Description
      };
    });

    contractData[selectedCustomerId] = newContractDetails;
    setContractList(contractData);
  };

  useEffect(() => {
    setAppPageDetails('home');
    setLogo(brand.logo || siteLogo['secondary_logos'][0]['image']['url']);

    // The Planned and Reactive pages can leave a filter applied to the filtered lists; start Home from the
    // full ones (this used to blank the planned list, which is what forced the reload).
    if(havePlanned) {
      updateFilteredPlannedJobList(plannedJobList);
    } else {
      updateFilteredPlannedJobList([]);
    }

    if(haveReactive) {
      updateFilteredReactiveJobList(reactiveJobList);
    }

    if(!haveContracts) {
      apiHelper.run(GetContractDataList, contractListCallback,[token,selectedCustomerId,'']);
    }

    // PORTAL-199: where the site is set up for it, Home's recent reactive list comes from the database and
    // there is NO fall back to the live Pronto call - a failure shows the section's could-not-load state.
    // A site not set up for it keeps the live call exactly as before.
    if(pmCallPagesEnabled()) {
      apiHelper.run(GetReactiveCallRecent, recentCallback, [token, userId, selectedCustomerId]);
      apiHelper.run(GetReactiveCallSlaSummary, slaSummaryCallback, [token, userId, selectedCustomerId]);
    } else {
      apiHelper.run(getServiceCallFunction, reactiveCallback,['',token,selectedCustomerId]);
    }

    if(pmCallPagesEnabled()) {
      apiHelper.run(GetReactiveCallSummary, reactiveSummaryCallback, [token, userId, selectedCustomerId]);
    }

    loadPlanned();
  },[]);

  useEffect(() => {

    const recentMode = pmCallPagesEnabled() && !recentFromPages;

    if(reactiveApiStatus == POPULATED || plannedApiStatus == POPULATED || plannedSummary !== null || reactiveSummary !== null || recentJobs !== null) {
      setImmediateApiStatus(POPULATED);
    }

    // In recent mode it is the recent query that says whether reactive is done; the paged set is only behind the SLA.
    const shownReactiveStatus = recentMode ? (recentJobs !== null ? POPULATED : recentStatus) : reactiveApiStatus;
    const reactiveDone = shownReactiveStatus == EMPTY || shownReactiveStatus == TIMEOUT;
    const plannedDone = plannedApiStatus == EMPTY || plannedApiStatus == TIMEOUT;

    if(reactiveDone && plannedDone) {
      const eitherTimedOut = shownReactiveStatus == TIMEOUT || plannedApiStatus == TIMEOUT;
      setImmediateApiStatus(eitherTimedOut ? TIMEOUT : EMPTY);
    }

  },[reactiveApiStatus, plannedApiStatus, plannedSummary, reactiveSummary, recentJobs, recentStatus, recentFromPages]);

  // What the "Recent Reactive Maintenance" section shows: the recent query's rows, or, where that has not answered or
  // found no entitlement, the list the page already holds (the paged set, filtered by contract).
  const recentMode = pmCallPagesEnabled() && !recentFromPages;
  const recentList = recentMode && recentJobs !== null ? recentJobs : filteredReactiveJobList;
  const recentShownStatus = recentMode ? (recentJobs !== null ? POPULATED : recentStatus) : reactiveApiStatus;

  // Where the indicator's counts come from: the database, unless the site is not set up for it or the person has no
  // entitlement there, when the list is counted in the browser as it always was.
  const slaFromDatabase = recentMode;
  const slaPending = slaFromDatabase ? !slaAnswered : reactiveApiStatus === CALLING;

  return (
    <PageLayout>
      <PageHeaderHome backgroundImg={(brand.heroImages && brand.heroImages['home']) || brand.heroImage || siteLogo.background_image[1]['image']['url']}>
        <PageHeaderText>{brand.welcomeText || 'Welcome to our Client portal'}</PageHeaderText>
        <PageHeaderImgCtn>
          <img src={logo} alt="Aquaheat logo" />
        </PageHeaderImgCtn>
      </PageHeaderHome>
      <PageCtn>
        <ContentCtn>
          {/* Everything that can be drawn without the data is drawn at once: headings, the cards (grey zeros) and the
              lists' frames. Each section shows a thin line only if its call is still running after two seconds. Only
              a page with no records at all, or a timeout, replaces the content with a message. */}
          {(immediateApiStatus === EMPTY || immediateApiStatus === TIMEOUT) ? <ApiStatus disable='' status={immediateApiStatus} /> : (
            <React.Fragment>
              <FullWidthCtn>
                <JobSummaryCardList plannedSummary={plannedSummary} reactiveSummary={reactiveSummary} />
              </FullWidthCtn>
              <FullWidthCtn>
                <SectionLoadingLine active={slaPending} />
                <SlaIndictorSum reactiveJobList={filteredReactiveJobList} pending={slaPending} summary={slaFromDatabase ? slaSummary : undefined} />
              </FullWidthCtn>
              <ReactiveListCtn>
                <HeadingCtn>
                  <span>Recent Reactive Maintenance<DataAsAt asOf={reactiveSummary !== null ? reactiveSummary.dataAsOfUtc : null} /></span>
                  <LinkToPage
                    onClick={(e): void => {
                      e.preventDefault();
                      setAppPageDetails('reactiveMaintenance');
                      navigate('/app/reactiveMaintenance');
                    }}
                  >
                    VIEW ALL
                  </LinkToPage>
                </HeadingCtn>
                <ApiStatus type='section' status={recentShownStatus}>
                  <JobsList itemList={recentList.slice(0, 4)} page="home" type='reactive'/>
                </ApiStatus>
                {recentShownStatus === CALLING && <GhostCards count={4} />}
              </ReactiveListCtn>
              <PlannedListCtn>
                <HeadingCtn>
                  <span>Recent Planned Maintenance<DataAsAt asOf={plannedAsOf} /></span>
                  <LinkToPage
                    onClick={(e): void => {
                      e.preventDefault();
                      setAppPageDetails('plannedMaintenance');
                      navigate('/app/plannedMaintenance');
                    }}
                  >
                    VIEW ALL
                  </LinkToPage>
                </HeadingCtn>
                <ApiStatus status={plannedApiStatus} type="section">
                  <JobsList itemList={filteredPlannedJobList.slice(0,4)} page="home" type='planned'/>
                </ApiStatus>
                {plannedApiStatus === CALLING && <GhostCards count={4} />}
              </PlannedListCtn>
            </React.Fragment>
          )}
        </ContentCtn>
      </PageCtn>
    </PageLayout>
  );
};

export default Home;
